import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, slashArc, shardBurst, glintScatter, groundCloud, shadowedSlash } from './burstBuilders';
import { groundZone, centerpiece } from './zoneBuilders';

// =============================================================================
// WARRIOR EFFECTS
// =============================================================================

/**
 * Whirlwind spin - spinning blade circle for whirlwind ability
 */
const WHIRLWIND_SPIN: VFXEffectConfig = {
  id: 'whirlwind_spin',
  name: 'Whirlwind Spin',
  category: 'burst',
  description: 'Spinning blade circle for warrior whirlwind ability',
  primitives: [
    // Outer spin ring (expanding, rotating)
    {
      type: 'ellipse',
      width: 80,
      height: 80,
      color: 0xAAAAAA,
      alpha: 0.3,
      depth: 93,
      stroke: { color: 0xCCCCCC, width: 2 },
      tween: {
        rotation: Math.PI * 2,
        alpha: 0,
        scale: 1.5,
        duration: 200,
        ease: 'Power2'
      }
    },
    // Blade streaks (elongated ellipses rotating outward)
    {
      type: 'ellipse',
      width: 30,
      height: 10,
      color: 0x888888,
      alpha: 0.8,
      depth: 95,
      tween: {
        rotation: Math.PI * 2,
        alpha: 0,
        duration: 200,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 50 }
    },
    // Steel spark particles
    {
      type: 'circle',
      radius: 3,
      color: 0xFFFFFF,
      alpha: 0.8,
      depth: 96,
      tween: {
        alpha: 0,
        scale: 0.3,
        duration: 250,
        ease: 'Power2'
      },
      scatter: { count: 10, radius: 60 }
    },
    // Wind dust ring (ground level)
    {
      type: 'circle',
      radius: 6,
      color: 0xBBAA88,
      alpha: 0.4,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 2.5,
        duration: 350,
        ease: 'Power2'
      },
      scatter: { count: 4, radius: 45 }
    }
  ]
};

/**
 * Charge trail - red streak trail for charge dash
 */
const CHARGE_TRAIL: VFXEffectConfig = {
  id: 'charge_trail',
  name: 'Charge Trail',
  category: 'burst',
  description: 'Red streak trail for warrior charge dash',
  primitives: [
    // Wide red motion blur
    {
      type: 'ellipse',
      width: 60,
      height: 20,
      color: 0xFF0000,
      alpha: 0.5,
      depth: 90,
      tween: {
        alpha: 0,
        scaleX: 2,
        scaleY: 0.4,
        duration: 300,
        ease: 'Power2'
      }
    },
    // Bright core streak
    {
      type: 'rect',
      width: 40,
      height: 4,
      color: 0xFF4444,
      alpha: 0.8,
      depth: 91,
      tween: {
        alpha: 0,
        scaleX: 2.5,
        scaleY: 0.2,
        duration: 200,
        ease: 'Power2'
      }
    },
    // Ember scatter
    {
      type: 'circle',
      radius: 4,
      color: 0xFF6644,
      alpha: 0.7,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 0.3,
        duration: 350,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 25 }
    },
    // Ground dust puffs
    {
      type: 'circle',
      radius: 6,
      color: 0xBBAA88,
      alpha: 0.3,
      depth: 89,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 400,
        ease: 'Power2'
      },
      scatter: { count: 3, radius: 15 }
    }
  ]
};

/**
 * Leap trail — airborne whoosh streaks for Heroic Leap's arc. Canonical id:
 * warrior.toml `[heroic_leap.vfx] cast_effect = "leap_trail"` (was an
 * unregistered id until 2026-06-12 — the travel emitted it to a silent
 * fallback). Lighter + airier than charge_trail: no ground dust, pale streaks.
 */
const LEAP_TRAIL: VFXEffectConfig = {
  id: 'leap_trail',
  name: 'Leap Trail',
  category: 'burst',
  description: 'Pale air-whoosh streaks trailing the Heroic Leap arc',
  primitives: [
    // Wide pale motion blur
    {
      type: 'ellipse',
      width: 50,
      height: 16,
      color: 0xDDDDCC,
      alpha: 0.4,
      depth: 90,
      tween: {
        alpha: 0,
        scaleX: 1.8,
        scaleY: 0.4,
        duration: 260,
        ease: 'Power2'
      }
    },
    // Bright core streak
    {
      type: 'rect',
      width: 32,
      height: 3,
      color: 0xFFEECC,
      alpha: 0.7,
      depth: 91,
      tween: {
        alpha: 0,
        scaleX: 2.2,
        scaleY: 0.2,
        duration: 180,
        ease: 'Power2'
      }
    },
    // Air wisp scatter
    {
      type: 'circle',
      radius: 3,
      color: 0xEEEEDD,
      alpha: 0.5,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 0.3,
        duration: 300,
        ease: 'Power2'
      },
      scatter: { count: 4, radius: 20 }
    }
  ]
};

/**
 * Ground impact — Heroic Leap's landing beat. Canonical id: warrior.toml
 * `[heroic_leap.vfx] impact_effect = "ground_impact"` + the
 * MOVEMENT_TRAVEL_CONFIG.leap.landingVfxId default (was unregistered — the
 * landing emit fell through to the legacy particle fallback). A lighter
 * cousin of GROUND_SLAM: same shockwave/dust language, smaller shake — the
 * AOE damage burst already provides the heavy hit feedback.
 */
const GROUND_IMPACT: VFXEffectConfig = {
  id: 'ground_impact',
  name: 'Ground Impact',
  category: 'burst',
  description: 'Landing shockwave + dust for leap arrivals',
  primitives: [
    // Shockwave ring
    {
      type: 'circle',
      radius: 30,
      color: 0xBB8844,
      alpha: 0.5,
      depth: 93,
      stroke: { color: 0xDDAA66, width: 2 },
      tween: {
        alpha: 0,
        scale: 2.2,
        duration: 350,
        ease: 'Power2'
      }
    },
    // Debris chips kicked up
    {
      type: 'rect',
      width: 4,
      height: 4,
      color: 0x886644,
      alpha: 0.7,
      depth: 95,
      tween: {
        y: -24,
        alpha: 0,
        rotation: Math.PI * 0.5,
        duration: 420,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 28 }
    },
    // Dust cloud
    {
      type: 'circle',
      radius: 12,
      color: 0xBBAA88,
      alpha: 0.4,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 2.6,
        duration: 500,
        ease: 'Power2'
      }
    }
  ],
  screenShake: { intensity: 6, duration: 200 }
};

/**
 * Sword trail - silver trail for thrown sword projectile
 */
const SWORD_TRAIL: VFXEffectConfig = {
  id: 'sword_trail',
  name: 'Sword Trail',
  category: 'burst',
  description: 'Silver trail for thrown sword projectile',
  primitives: [
    // Metal glint trail
    {
      type: 'ellipse',
      width: 40,
      height: 12,
      color: 0xEEEEEE,
      alpha: 0.6,
      depth: 95,
      tween: {
        alpha: 0,
        scaleX: 1.5,
        scaleY: 0.4,
        duration: 200,
        ease: 'Power2'
      }
    },
    // Sharp edge sparks
    {
      type: 'star',
      points: 4,
      innerRadius: 2,
      outerRadius: 5,
      color: 0xFFFFFF,
      alpha: 0.8,
      depth: 97,
      tween: {
        rotation: Math.PI * 0.25,
        alpha: 0,
        duration: 200,
        ease: 'Power2'
      },
      scatter: { count: 3, radius: 12 }
    },
    // Trailing white particles
    {
      type: 'circle',
      radius: 3,
      color: 0xFFFFFF,
      alpha: 0.6,
      depth: 96,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 250,
        ease: 'Power2'
      },
      scatter: { count: 4, radius: 15 }
    }
  ]
};

/**
 * Defensive aura - gray protective barrier for defensive stance
 */
const DEFENSIVE_AURA: VFXEffectConfig = {
  id: 'defensive_aura',
  name: 'Defensive Aura',
  category: 'aura',
  description: 'Gray protective barrier for defensive stance buff',
  followTarget: 'player',
  primitives: [
    // Shield ellipse shell (pulsing)
    {
      type: 'ellipse',
      width: 70,
      height: 80,
      color: 0x888888,
      alpha: 0.15,
      depth: 50,
      stroke: { color: 0xAAAAAA, width: 3 },
      tween: {
        rotation: Math.PI * 0.1,
        scale: 1.05,
        duration: 200,
        ease: 'Quad.easeInOut',
        yoyo: true,
        repeat: -1
      }
    },
    // Steel fragments orbiting
    {
      type: 'rect',
      width: 6,
      height: 2,
      color: 0xCCCCCC,
      alpha: 0.5,
      depth: 51,
      tween: {
        alpha: 0.3,
        rotation: Math.PI * 0.5,
        duration: 600,
        yoyo: true,
        repeat: -1
      },
      scatter: { count: 6, radius: 45 }
    },
    // Sparks at the barrier edge
    {
      type: 'circle',
      radius: 2,
      color: 0xFFFFFF,
      alpha: 0.6,
      depth: 52,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 500,
        repeat: -1,
        delay: 150
      },
      scatter: { count: 4, radius: 40 }
    }
  ]
};

/**
 * Battle cry burst - red explosive burst for battle cry
 */
const BATTLE_CRY_BURST: VFXEffectConfig = {
  id: 'battle_cry_burst',
  name: 'Battle Cry Burst',
  category: 'burst',
  description: 'Red explosive burst for warrior battle cry activation',
  primitives: [
    // Shockwave ring (expanding with stroke)
    {
      type: 'circle',
      radius: 50,
      color: 0xFF0000,
      alpha: 0.5,
      depth: 94,
      stroke: { color: 0xFF4444, width: 3 },
      tween: {
        alpha: 0,
        scale: 2.5,
        duration: 500,
        ease: 'Power2'
      }
    },
    // Ground impact wave (flat ellipse)
    {
      type: 'ellipse',
      width: 80,
      height: 20,
      color: 0xFF4444,
      alpha: 0.5,
      depth: 93,
      tween: {
        alpha: 0,
        scaleX: 3,
        scaleY: 0.3,
        duration: 450,
        ease: 'Power2'
      }
    },
    // Inner red core flash
    {
      type: 'circle',
      radius: 20,
      color: 0xFFAAAA,
      alpha: 0.8,
      depth: 96,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 200,
        ease: 'Power1'
      }
    },
    // Red ember scatter
    {
      type: 'circle',
      radius: 4,
      color: 0xFF6666,
      alpha: 0.8,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 0.3,
        duration: 600,
        ease: 'Power2'
      },
      scatter: { count: 12, radius: 65 }
    }
  ],
  screenShake: { intensity: 6, duration: 250 }
};

/**
 * Ground slam - heavy melee AOE impact
 */
const GROUND_SLAM: VFXEffectConfig = {
  id: 'ground_slam',
  name: 'Ground Slam',
  category: 'burst',
  description: 'Heavy ground impact with shockwave and debris',
  primitives: [
    // Shockwave ring
    {
      type: 'circle',
      radius: 40,
      color: 0xBB8844,
      alpha: 0.5,
      depth: 93,
      stroke: { color: 0xDDAA66, width: 2 },
      tween: {
        alpha: 0,
        scale: 2.5,
        duration: 400,
        ease: 'Power2'
      }
    },
    // Ground crack lines (elongated rects radiating out)
    {
      type: 'rect',
      width: 30,
      height: 2,
      color: 0xAA8855,
      alpha: 0.8,
      depth: 94,
      tween: {
        alpha: 0,
        scaleX: 2,
        duration: 350,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 40 }
    },
    // Debris chunks flying upward
    {
      type: 'rect',
      width: 5,
      height: 5,
      color: 0x886644,
      alpha: 0.7,
      depth: 96,
      tween: {
        y: -30,
        alpha: 0,
        rotation: Math.PI * 0.5,
        duration: 500,
        ease: 'Power2'
      },
      scatter: { count: 8, radius: 35 }
    },
    // Dust cloud
    {
      type: 'circle',
      radius: 15,
      color: 0xBBAA88,
      alpha: 0.4,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 3,
        duration: 600,
        ease: 'Power2'
      }
    }
  ],
  screenShake: { intensity: 10, duration: 300 }
};

/**
 * Sword Swing — quick melee slash arc for warrior auto-attack
 */
const SWORD_SWING: VFXEffectConfig = {
  id: 'sword_swing',
  name: 'Sword Swing',
  category: 'burst',
  description: 'Quick slash arc for warrior auto-attack',
  primitives: [
    {
      type: 'arc',
      arcStartAngle: -30,
      arcEndAngle: 30,
      arcRadius: 40,
      color: 0xCCCCCC,
      alpha: 0.6,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 1.3,
        duration: 200,
        ease: 'Power2'
      }
    }
  ]
};

/**
 * Execute slash - dark red finishing blow arc
 */
const EXECUTE_SLASH: VFXEffectConfig = {
  id: 'execute_slash',
  name: 'Execute Slash',
  category: 'burst',
  description: 'Dark red finishing blow for execute ability',
  screenShake: { intensity: 8, duration: 250 },
  primitives: [
    // Heavy downward slash arc
    { type: 'ellipse', width: 65, height: 12, color: 0x8B0000, alpha: 0.9, depth: 95,
      tween: { rotation: Math.PI * 0.6, alpha: 0, scaleX: 1.5, scaleY: 0.3, duration: 200, ease: 'Power2' } },
    // Blood-red burst at impact
    { type: 'circle', radius: 25, color: 0xCC0000, alpha: 0.7, depth: 94,
      tween: { alpha: 0, scale: 2, duration: 300, ease: 'Power2' } },
    // White flash core
    { type: 'circle', radius: 10, color: 0xFFAAAA, alpha: 0.9, depth: 96,
      tween: { alpha: 0, scale: 1.5, duration: 150, ease: 'Power1' } },
    // Blood splatter fragments
    { type: 'circle', radius: 4, color: 0x660000, alpha: 0.8, depth: 95,
      tween: { alpha: 0, scale: 0.3, duration: 400, ease: 'Power2' },
      scatter: { count: 6, radius: 30 } }
  ]
};

/**
 * Mortal wound - bleeding wound effect with lingering red glow
 */
const MORTAL_WOUND: VFXEffectConfig = {
  id: 'mortal_wound',
  name: 'Mortal Wound',
  category: 'burst',
  description: 'Bleeding wound effect for mortal strike ability',
  primitives: [
    // Deep cut slash
    { type: 'ellipse', width: 45, height: 6, color: 0xFF0000, alpha: 0.8, depth: 95,
      tween: { rotation: Math.PI * 0.4, alpha: 0, scaleX: 1.3, duration: 250, ease: 'Power2' } },
    // Wound glow
    { type: 'circle', radius: 18, color: 0xCC0000, alpha: 0.5, depth: 93,
      tween: { alpha: 0, scale: 1.8, duration: 500, ease: 'Power2' } },
    // Blood drops
    { type: 'circle', radius: 3, color: 0x880000, alpha: 0.9, depth: 96,
      tween: { y: 15, alpha: 0, scale: 0.5, duration: 600, ease: 'Power2' },
      scatter: { count: 5, radius: 20 } },
    // Entity tint to show wounded state
    { type: 'entity_override', entityTint: 0xFF0000, tween: { duration: 300 } }
  ]
};

// =============================================================================
// FX grammar buildout — Lane 5 (warrior physical), 2026-07-17 plan.
// Composed from burstBuilders verbs; ≤2 bespoke flavor primitives per def.
// =============================================================================

/**
 * Blade tornado — WHIRLWIND_SPIN's bigger, longer sibling (the 6 aliased
 * abilities get their own read). Wider expanding steel rings, three slashArc
 * sweeps at staggered delays (the "tornado" rotation read), steel-chip debris
 * + glints, wind dust. ~560ms total vs whirlwind's ~350ms.
 */
const BLADE_TORNADO: VFXEffectConfig = {
  id: 'blade_tornado',
  name: 'Blade Tornado',
  category: 'burst',
  description: 'Big sustained steel vortex — staggered blade sweeps inside expanding rings',
  primitives: [
    // Inner steel ring — the first, brightest expansion beat.
    ringBlast({
      radius: 46, color: 0xCCCCCC, alpha: 0.55, depth: 94,
      stroke: { color: 0xEEF2F5, width: 3 },
      expandTo: 2.6, durationMs: 420,
    }),
    // Bespoke flavor: second, paler outer ring on a delay — the vortex GROWS
    // (ringBlast has no delay knob; this is the def's one-off).
    {
      type: 'circle', radius: 60, color: 0xEEF2F5, alpha: 0.35, depth: 93,
      stroke: { color: 0xCCCCCC, width: 2 },
      tween: { alpha: 0, scale: 2.4, duration: 380, ease: 'Power2', delay: 160 },
    },
    // Three staggered blade sweeps at rotating angle windows — the tornado read.
    slashArc({ radius: 52, startDeg: -35, endDeg: 45, width: 7, color: 0xCCCCCC, durationMs: 260 }),
    slashArc({ radius: 58, startDeg: 130, endDeg: 215, width: 7, color: 0xEEF2F5, durationMs: 260, fadeDelayMs: 120 }),
    slashArc({ radius: 64, startDeg: 40, endDeg: 125, width: 6, color: 0xCCCCCC, durationMs: 280, fadeDelayMs: 240, glow: true }),
    // Steel-chip debris thrown from the vortex.
    shardBurst({ count: 8, spread: 55, color: 0x9AA4AA, shard: { width: 8, height: 2 }, durationMs: 360 }),
    // Debris glints — the late finishing sparkle.
    glintScatter({ count: 10, spread: 70, delayMs: 150, durationMs: 260 }),
    // Wind dust kicked up at the feet.
    groundCloud({ width: 90, height: 22, color: 0xBBAA88, alpha: 0.35, durationMs: 560 }),
  ],
};

/**
 * Heavy slash — the WEIGHT contrast to quick_slash (CommonVFX lane). One
 * THICK slow blade arc with a trailing shadow edge, a short screen-weight
 * ring at contact, dust at the feet. Reads like a two-hander, not a dagger.
 */
const HEAVY_SLASH: VFXEffectConfig = {
  id: 'heavy_slash',
  name: 'Heavy Slash',
  category: 'burst',
  description: 'Thick slow two-hander arc with shadow edge, contact ring, and ground dust',
  screenShake: { intensity: 7, duration: 220 },
  primitives: [
    // The blade: one thick, SLOW arc (vs quick_slash's thin fast flick).
    slashArc({ radius: 56, startDeg: -70, endDeg: 40, width: 16, color: 0xD8D8D8, alpha: 0.95, durationMs: 340, glow: true }),
    // Shadow edge dragging just behind and outside the blade.
    slashArc({ radius: 62, startDeg: -70, endDeg: 40, width: 10, color: 0x777777, alpha: 0.6, depth: 95, durationMs: 380, fadeDelayMs: 40 }),
    // Short screen-weight ring blast at contact.
    ringBlast({ radius: 24, color: 0x999999, alpha: 0.6, stroke: { color: 0xEEEEEE, width: 2 }, expandTo: 1.9, durationMs: 220 }),
    // Contact sparks.
    glintScatter({ count: 5, spread: 34, durationMs: 200 }),
    // Dust shoved out along the ground by the follow-through.
    groundCloud({ width: 76, height: 18, color: 0xBBAA88, alpha: 0.4, durationMs: 480 }),
  ],
};

// =============================================================================
// K21 art heat-map physical slice (2026-07-17) — bespoke defs for the
// highest-signature consumers of the shared quick_slash / blade_tornado /
// heavy_slash generics. Steel-grey/white slash language + one accent per
// ability. Warrior grammar: heavier, wider arcs + ground impact dust.
// All blade reads use the degrees vocab (slashArc: arcRadius/arcStartAngle/
// arcEndAngle) so a future weaponSweepTrail verb can layer onto the same
// angle windows without repainting these defs.
// =============================================================================

/**
 * Bladestorm cyclone — the signature spinning multi-arc. Four blade sweeps
 * marching around the full circle at staggered delays (the sustained-spin
 * read), a double ring that GROWS, steel chips, wide wind dust.
 * Accent: cold wind-steel blue. Cast beat for bladestorm + whirlwind_improved.
 */
const BLADESTORM_CYCLONE: VFXEffectConfig = {
  id: 'bladestorm_cyclone',
  name: 'Bladestorm Cyclone',
  category: 'burst',
  description: 'Four staggered blade sweeps marching a full circle inside growing steel rings',
  screenShake: { intensity: 5, duration: 200 },
  primitives: [
    // First expansion ring — bright steel.
    ringBlast({
      radius: 42, color: 0xC8CED4, alpha: 0.5, depth: 93,
      stroke: { color: 0xF2F6F8, width: 3 }, expandTo: 2.4, durationMs: 400,
    }),
    // Second, wind-steel-blue ring on a delay — the cyclone GROWS.
    ringBlast({
      radius: 56, color: 0x9FC4DC, alpha: 0.35, depth: 92,
      stroke: { color: 0xC8CED4, width: 2 }, expandTo: 2.2, durationMs: 360, delayMs: 170,
    }),
    // Four blade sweeps marching around the circle — 90° apart, staggered.
    slashArc({ radius: 50, startDeg: -40, endDeg: 40, width: 8, color: 0xF2F6F8, durationMs: 220 }),
    slashArc({ radius: 54, startDeg: 50, endDeg: 130, width: 7, color: 0xC8CED4, durationMs: 230, fadeDelayMs: 90 }),
    slashArc({ radius: 58, startDeg: 140, endDeg: 220, width: 7, color: 0x9FC4DC, durationMs: 230, fadeDelayMs: 180 }),
    slashArc({ radius: 62, startDeg: 230, endDeg: 310, width: 6, color: 0xF2F6F8, durationMs: 240, fadeDelayMs: 270, glow: true }),
    // Steel chips flung from the storm.
    shardBurst({ count: 9, spread: 62, color: 0x8B959C, shard: { width: 9, height: 2 }, durationMs: 340 }),
    // Metal glints riding the late sweeps.
    glintScatter({ count: 8, spread: 66, delayMs: 200, durationMs: 240 }),
    // Wide wind dust shoved out at the feet.
    groundCloud({ width: 104, height: 24, color: 0xBBAA88, alpha: 0.35, swellTo: 1.7, durationMs: 580 }),
  ],
};

/**
 * Ravager whirl — the thrown whirling blade LANDS. Tight three-arc spin at
 * the target (smaller radius than the cyclone — this is a weapon, not a
 * storm), burnished-bronze accent on the haft, chip shards, contact dust.
 */
const RAVAGER_WHIRL: VFXEffectConfig = {
  id: 'ravager_whirl',
  name: 'Ravager Whirl',
  category: 'burst',
  description: 'Tight spinning three-arc blade impact with bronze haft glint and chip spray',
  screenShake: { intensity: 4, duration: 150 },
  primitives: [
    // Warm energy core — "fury behind the steel" (VQA-05 legibility follow-up,
    // 2026-07-17: A1 late-capture finally photographed the whirl and the
    // all-steel/bronze arcs still read near-invisible on the dark grounds).
    // A glowing amber halo gives the spin a hot core that carries at gameplay
    // zoom, matching overpower_crush's ember-behind-steel. The steel arcs still
    // render on top, so the metallic weapon identity is preserved.
    ringBlast({
      radius: 30, color: 0xE0803A, alpha: 0.5, depth: 93, glow: true,
      expandTo: 1.7, durationMs: 300,
    }),
    // Contact ring where the ravager bites in. Gameplay-zoom readability
    // tune (VQA-05, 2026-07-17 clean recapture): the whirl was near-invisible
    // at distance — alpha/width/glow raised, structure and tempo unchanged.
    ringBlast({
      radius: 26, color: 0xC8CED4, alpha: 0.75, depth: 94,
      stroke: { color: 0xF2F6F8, width: 3 }, expandTo: 2.0, durationMs: 240,
    }),
    // Three tight sweeps — 120° apart, staggered: the embedded weapon still spins.
    slashArc({ radius: 38, startDeg: -30, endDeg: 55, width: 8, color: 0xF2F6F8, alpha: 0.95, durationMs: 200, glow: true }),
    slashArc({ radius: 42, startDeg: 90, endDeg: 175, width: 8, color: 0xC8A05A, durationMs: 210, fadeDelayMs: 80, glow: true }),
    slashArc({ radius: 46, startDeg: 210, endDeg: 295, width: 7, color: 0xC8CED4, durationMs: 220, fadeDelayMs: 160, glow: true }),
    // Chipped-steel splinters (pointed, not rects — a blade digging in).
    shardBurst({
      count: 7, spread: 44, color: 0xB9C2C8, alpha: 0.95,
      shard: { points: [0, -10, 4, 4, -3, 5] },
      stroke: { color: 0xF2F6F8, width: 1 }, durationMs: 300, delayMs: 60,
    }),
    // Bronze glint off the haft.
    glintScatter({ count: 5, spread: 40, color: 0xE8C88A, delayMs: 120, durationMs: 220 }),
    // Small dust puff at the bite point.
    groundCloud({ width: 62, height: 16, color: 0xBBAA88, alpha: 0.32, durationMs: 440 }),
  ],
};

/**
 * Overpower crush — the warrior's heaviest single arc: one THICK descending
 * blade with a dragging shadow edge, contact ring, debris kicked upward,
 * heavy dust. Accent: hot ember-bronze at contact (fury behind the steel).
 */
const OVERPOWER_CRUSH: VFXEffectConfig = {
  id: 'overpower_crush',
  name: 'Overpower Crush',
  category: 'burst',
  description: 'One massive descending blade arc with shadow drag, ember contact ring, and debris',
  screenShake: { intensity: 8, duration: 240 },
  primitives: [
    // The blade + its dragging shadow edge (shared angle window, sweep-layerable).
    // Gameplay-zoom readability tune (VQA-05, 2026-07-17 clean recapture):
    // the dark shadow drag drowned the blade on dark grounds — blade up,
    // drag eased, ember ring hotter. Weight identity (thick slow arc +
    // shadow) preserved.
    ...shadowedSlash({
      radius: 58, startDeg: -110, endDeg: 15,
      blade: { color: 0xE4E8EB, width: 20, alpha: 0.95, glow: true },
      trail: { color: 0x6E7880, width: 10, alpha: 0.45, delayMs: 50, radiusOffset: 6 },
      durationMs: 330,
    }),
    // Ember-bronze contact ring — the crush lands HOT.
    ringBlast({
      radius: 24, color: 0xE07840, alpha: 0.75, depth: 94,
      stroke: { color: 0xF2F6F8, width: 2 }, expandTo: 2.1, durationMs: 250,
    }),
    // Debris chunks kicked up by the follow-through.
    shardBurst({ count: 6, spread: 36, color: 0x8B7355, shard: { width: 5, height: 4 }, durationMs: 380, delayMs: 60 }),
    // Contact sparks.
    glintScatter({ count: 6, spread: 34, durationMs: 210, delayMs: 40 }),
    // Heavy dust shoved flat along the ground.
    groundCloud({ width: 88, height: 20, color: 0xBBAA88, alpha: 0.42, swellTo: 1.6, durationMs: 520 }),
  ],
};

/**
 * Skullsplitter rend — a steep vertical cleave: thick top-down arc crossed
 * by a fast counter-cut, with a thin crimson bite line on a delay (the
 * split reads AFTER the steel). No contact ring — the wound is the beat.
 */
const SKULLSPLITTER_REND: VFXEffectConfig = {
  id: 'skullsplitter_rend',
  name: 'Skullsplitter Rend',
  category: 'burst',
  description: 'Steep vertical cleave crossed by a counter-cut, crimson bite line landing late',
  screenShake: { intensity: 7, duration: 200 },
  primitives: [
    // The cleave: thick, steep, through the top of the arc basis.
    slashArc({ radius: 54, startDeg: -135, endDeg: -35, width: 15, color: 0xE4E8EB, alpha: 0.95, durationMs: 300, glow: true }),
    // Fast counter-cut crossing it — simultaneous, thinner.
    slashArc({ radius: 44, startDeg: 205, endDeg: 130, width: 7, color: 0xB9C2C8, durationMs: 190 }),
    // The crimson bite line — lands late, right in the cleave's window.
    slashArc({ radius: 50, startDeg: -125, endDeg: -45, width: 4, color: 0xB03030, alpha: 0.85, durationMs: 260, fadeDelayMs: 110 }),
    // Bone-white splinters thrown from the split.
    shardBurst({
      count: 6, spread: 40, color: 0xEDE6DA, alpha: 0.9,
      shard: { points: [0, -9, 3, 3, -3, 4] }, durationMs: 320, delayMs: 90,
    }),
    // Sparks off the edge.
    glintScatter({ count: 5, spread: 30, durationMs: 200 }),
    // Modest dust — the hit is vertical, not a ground shove.
    groundCloud({ width: 58, height: 14, color: 0xBBAA88, alpha: 0.3, durationMs: 420 }),
  ],
};

/**
 * Onslaught breaker — a charging strike that arrives flat and forward: two
 * shallow horizontal arcs, a DELAYED impact ring (momentum lands a beat
 * after the blade), a long low skid of dust, ember glints. Accent: raging
 * ember-orange.
 */
const ONSLAUGHT_BREAKER: VFXEffectConfig = {
  id: 'onslaught_breaker',
  name: 'Onslaught Breaker',
  category: 'burst',
  description: 'Flat forward double-arc with delayed momentum ring and a long dust skid',
  screenShake: { intensity: 6, duration: 200 },
  primitives: [
    // Twin shallow forward arcs — the charge's stacked momentum.
    slashArc({ radius: 52, startDeg: -20, endDeg: 40, width: 11, color: 0xE4E8EB, alpha: 0.95, durationMs: 220, glow: true }),
    slashArc({ radius: 44, startDeg: -30, endDeg: 30, width: 8, color: 0xC8CED4, durationMs: 240, fadeDelayMs: 70 }),
    // Momentum ring — DELAYED: the mass arrives after the edge.
    ringBlast({
      radius: 28, color: 0xFF8855, alpha: 0.5, depth: 93,
      stroke: { color: 0xF2F6F8, width: 2 }, expandTo: 2.2, durationMs: 280, delayMs: 120,
    }),
    // Steel chips thrown forward off the impact.
    shardBurst({ count: 7, spread: 48, color: 0x8B959C, shard: { width: 8, height: 2 }, durationMs: 320 }),
    // Raging ember glints.
    glintScatter({ count: 6, spread: 44, color: 0xFFB080, delayMs: 140, durationMs: 240 }),
    // Long LOW dust skid — the charge plows the ground.
    groundCloud({ width: 118, height: 13, color: 0xBBAA88, alpha: 0.4, swellTo: 1.8, durationMs: 560 }),
  ],
};

/**
 * Wild strike flurry — the warrior's QUICK hit, but heavier than the rogue
 * read: three mid-width arcs (two crossing, one late), doubled glints, a
 * small dust puff (a warrior's boots always move earth). Accent: raw
 * white-hot edge, no color — fury is speed here.
 */
const WILD_STRIKE_FLURRY: VFXEffectConfig = {
  id: 'wild_strike_flurry',
  name: 'Wild Strike Flurry',
  category: 'burst',
  description: 'Three crossing mid-width steel arcs with doubled glints and a boot-dust puff',
  primitives: [
    // Crossing opening pair — simultaneous.
    slashArc({ radius: 42, startDeg: -55, endDeg: 35, width: 6, color: 0xF2F6F8, durationMs: 170, glow: true }),
    slashArc({ radius: 38, startDeg: 215, endDeg: 125, width: 5, color: 0xC8CED4, durationMs: 180 }),
    // The wild third cut — late, wider, off-axis.
    slashArc({ radius: 46, startDeg: -95, endDeg: -15, width: 7, color: 0xE4E8EB, durationMs: 200, fadeDelayMs: 90 }),
    // Doubled glint beats — opening sparks then follow-through sparks.
    glintScatter({ count: 4, spread: 28, durationMs: 160 }),
    glintScatter({ count: 5, spread: 40, delayMs: 110, durationMs: 200 }),
    // Small boot-dust puff — the warrior tell.
    groundCloud({ width: 48, height: 12, color: 0xBBAA88, alpha: 0.28, durationMs: 360 }),
  ],
};

// =============================================================================
// Caster auras (VFX-evolution M2, theme F) — character-anchored GLOW that
// tracks the caster (followTarget:'player' → FollowerSystem) while the
// ability is active. DJ: "nothing happening with our character like glows
// and auras." Played via AbilityBuilder.withCasterAura → vfx:status_effect.
// All glow:true through the bloom layer. Looping primitives (repeat:-1) are
// bounded by the def `duration` + the withCasterAura OFF emit.
// =============================================================================

const CHARGE_SURGE: VFXEffectConfig = {
  id: 'charge_surge',
  name: 'Charge Surge',
  category: 'aura',
  followTarget: 'player',
  duration: 600,
  primitives: [
    // Fiery surge glow pulsing around the warrior.
    { type: 'circle', radius: 30, color: 0xff6622, alpha: 0.45, glow: true,
      tween: { scale: 1.2, alpha: 0.2, duration: 220, ease: 'Sine.easeInOut', yoyo: true, repeat: -1 } },
    // Rising embers.
    { type: 'circle', radius: 4, color: 0xffaa44, alpha: 0.8, glow: true, scatter: { count: 5, radius: 26 },
      tween: { y: -34, alpha: 0, duration: 500, ease: 'Power2', repeat: -1 } },
  ],
};

const HEROIC_AURA: VFXEffectConfig = {
  id: 'heroic_aura',
  name: 'Heroic Aura',
  category: 'aura',
  followTarget: 'player',
  duration: 800,
  primitives: [
    // Radiant golden glow.
    { type: 'circle', radius: 34, color: 0xffcc44, alpha: 0.45, glow: true,
      tween: { scale: 1.15, alpha: 0.2, duration: 320, ease: 'Sine.easeInOut', yoyo: true, repeat: -1 } },
    // Orbiting spark stars.
    { type: 'star', points: 5, innerRadius: 3, outerRadius: 7, color: 0xffe088, alpha: 0.8, glow: true,
      orbit: { radius: 32, speed: 1.6 } },
  ],
};

const WHIRLWIND_AURA: VFXEffectConfig = {
  id: 'whirlwind_aura',
  name: 'Whirlwind Aura',
  category: 'aura',
  followTarget: 'player',
  duration: 600,
  primitives: [
    // Spinning steel ring (the visible spin DJ asked for).
    { type: 'ellipse', width: 72, height: 72, color: 0x88ccdd, alpha: 0.32, glow: true,
      tween: { rotation: Math.PI * 2, duration: 350, ease: 'Linear', repeat: -1 } },
    // Orbiting blade streaks.
    { type: 'rect', width: 28, height: 5, color: 0xcce6f0, alpha: 0.75, glow: true,
      orbit: { radius: 34, speed: 4.5 } },
  ],
};

/**
 * Skull Banner — planted war banner rallying the party (zone-def
 * re-categorization batch; first registered def — the SummonBehavior vfxId
 * 'skull_banner' was unregistered → legacy particle fallback). Crimson
 * rally field + swaying banner centerpiece. category:'zone' under the
 * wave-4 lifetime contract: world-anchored, duration ceiling mandatory.
 * Duration = the SUMMON's 15s (SkullBannerAbility.ts); the 20s TOML
 * duration_ms mismatch is a content-lane fix.
 */
const SKULL_BANNER: VFXEffectConfig = {
  id: 'skull_banner',
  name: 'Skull Banner',
  category: 'zone',
  description: 'Planted war banner rallying nearby allies',
  duration: 15000,
  primitives: [
    ...groundZone({
      ring: { width: 160, fill: 0x661111, rim: 0xAA2222, alpha: 0.14, pulse: { alpha: 0.24, duration: 1400 } },
      pulse: { radius: 32, color: 0xCC3333, alpha: 0.28, periodMs: 1400, expandTo: 2.0 },
    }),
    // The staff — dark ash pole planted at the ground plane
    centerpiece({
      shape: 'rect', width: 6, height: 58,
      color: 0x3A2A20, accent: 0x6B4F3A, alpha: 0.95,
    }),
    // The banner cloth — crimson field swaying off the staff
    centerpiece({
      shape: 'rect', width: 26, height: 20,
      color: 0x8B1A1A, accent: 0xEEDDCC, alpha: 0.9, depth: 15,
      offsetY: -34,
      sway: { rotation: 0.1, duration: 900 },
    }),
    // The skull sigil — bone-white glint on the cloth
    centerpiece({
      shape: 'star', width: 6,
      color: 0xEEDDCC, accent: 0xFFFFFF, alpha: 0.9, depth: 16,
      offsetY: -34,
      flicker: { alpha: 0.6, duration: 900 },
    }),
  ],
};

/**
 * TOML cast_effect id — warrior.toml authors `cast_effect = "banner_skull"`,
 * which outranks the map on the remote path (wave-4 cast-side doctrine).
 * Register the same zone def under that id so remote screens render the
 * banner instead of a generator realization.
 */
const SKULL_BANNER_CAST: VFXEffectConfig = {
  ...SKULL_BANNER,
  id: 'banner_skull',
  description: 'Skull banner as authored by the TOML cast_effect id',
};

// =============================================================================
// EXPORTS
// =============================================================================

export const WARRIOR_VFX: VFXEffectConfig[] = [
  WHIRLWIND_SPIN,
  CHARGE_TRAIL,
  LEAP_TRAIL,
  GROUND_IMPACT,
  SWORD_TRAIL,
  DEFENSIVE_AURA,
  BATTLE_CRY_BURST,
  GROUND_SLAM,
  SWORD_SWING,
  EXECUTE_SLASH,
  MORTAL_WOUND,
  BLADE_TORNADO,
  HEAVY_SLASH,
  BLADESTORM_CYCLONE,
  RAVAGER_WHIRL,
  OVERPOWER_CRUSH,
  SKULLSPLITTER_REND,
  ONSLAUGHT_BREAKER,
  WILD_STRIKE_FLURRY,
  CHARGE_SURGE,
  HEROIC_AURA,
  WHIRLWIND_AURA,
  SKULL_BANNER,
  SKULL_BANNER_CAST,
];
